/* eslint-disable max-len */
/* eslint-disable class-methods-use-this */
import React from 'react';
import { useRouteData, useSiteData, Head } from 'react-static';
// template components
import BulletedList from '@nmx/template/dist/componentLibrary/components/BulletedList';
import Col from '@nmx/template/dist/componentLibrary/foundations/Grid/Col';
import DescriptionList from '@nmx/template/dist/componentLibrary/components/DescriptionList';
import Link from '@nmx/template/dist/componentLibrary/components/Link';
import Page from '@nmx/template/dist/componentLibrary/layout/Page';
import Row from '@nmx/template/dist/componentLibrary/foundations/Grid/Row';
import SectionContainer from '@nmx/template/dist/componentLibrary/components/SectionContainer';
import Typography from '@nmx/template/dist/componentLibrary/foundations/Typography';
import WidowBlock from '@nmx/template/dist/componentLibrary/components/WidowBlock';
// native components
import LegalLinks from '../../components/legalLinks';
import ClientServicesPhone4644, { clientServicesPhoneNumberLinkUrl } from '../../components/clientServicesPhone4644/clientServicesPhone4644';

const bulletedListConfigTrackingTechnology = [
  {
    bulletText: (<>
      <strong>Google Analytics.</strong> You may review&nbsp;
      <Link
        linkUrl='https://policies.google.com/technologies/partner-sites/'
        isOutgoingLink
        variant='primaryDeemphasize'>
        how Google uses information from websites that use its services&nbsp;
      </Link>
      and&nbsp;
      <Link
        linkUrl='https://support.google.com/analytics/answer/6004245'
        isOutgoingLink
        variant='primaryDeemphasize'>
        how Google safeguards your data
      </Link>
      . To opt&#45;out of being tracked by Google Analytics, you may download and install the Google Analytics Opt&#45;out Browser Add&#45;on.
    </>),
  },
  {
    bulletText: (<>
      <strong>Adobe Analytics.</strong> You may review Adobe&#39;s&nbsp;
      <Link
        linkUrl='https://www.adobe.com/privacy/privacy-contact.html'
        isOutgoingLink
        variant='primaryDeemphasize'>
        privacy FAQ&nbsp;
      </Link>
      and learn more about Adobe&#39;s&nbsp;
      <Link
        linkUrl='https://www.adobe.com/privacy/opt-out.html'
        isOutgoingLink
        variant='primaryDeemphasize'>
        privacy policy.
      </Link>
    </>),
  },
  {
    bulletText: (<>
      <strong>Heap Analytics.</strong> You may review Heap&#39;s&nbsp;
      <Link
        linkUrl='https://heap.io/privacy/'
        isOutgoingLink
        variant='primaryDeemphasize'>
        privacy policy.
      </Link>
    </>),
  },
];

export default () => {
  const { config, dynamicConfig } = useSiteData();
  const { pageProps } = useRouteData();

  return (
    <Page
      {...pageProps} // defined in route.js
      headComponent={Head}
      dynamicConfig={dynamicConfig}
      config={config}
      breadcrumbsNavigationConfig={[
        {
          pageName: 'Home',
          linkUrl: '/',
          linkAriaLabel: 'Northwestern Mutual Home',
        },
        {
          pageName: 'Online Privacy Statement',
          linkUrl: '/online-privacy-statement/',
          linkAriaLabel: 'Northwestern Mutual Online Privacy Statement',
        },
      ]}>
      <SectionContainer
        id='online-privacy-statement-intro'
        backgroundVariant='lightA'
        themeType='lightTheme'
        disableBottomPadding>
        <Row align='center'>
          <Col medium={8}>
            <Typography
              component='h1'
              nextElement='typography'>
              Online Privacy Statement
            </Typography>
            <Typography nextElement='typography'>
              <strong>Effective Date:</strong> December 9, 2022
            </Typography>
            <Typography nextElement='typography'>
              <strong>Last Revised:</strong> December 9, 2022
            </Typography>
            <Typography nextElement='typography'>
              <Link
                id='online-privacy-statement-pdf-link'
                variant='primaryDeemphasize'
                linkUrl='/assets/pdf/legal/online-privacy-statement.pdf'
                ariaLabel='Download the email online privacy statement pdf.'
                isInternalPdf>
                Printable version
              </Link>
            </Typography>
            <Typography nextElement='typography'>
              This Online Privacy Statement &#40;&#34;Statement&#34;&#41; describes how we collect, share and use personal information. This Statement applies to &#40;a&#41; the public&#45;facing website available at northwesternmutual.com and the pages accessible during and after a user &#40;&#34;you&#34; or &#34;your&#34;&#41; logs in to a private section of the website &#40;the &#34;Client Website&#34; and &#34;Client Mobile Application&#34;&#41;, &#40;b&#41; websites owned or operated by the Northwestern Mutual Family of Companies, and &#40;c&#41; separate websites owned or operated by Northwestern Mutual Investment Management Company, LLC &#40;&#34;Subsidiary Website&#34;&#41;. All websites described in the prior sentences are defined collectively for the purposes of this Statement as the &#34;Websites&#34;.
            </Typography>
            <Typography nextElement='typography'>
              Northwestern Mutual Family of Companies within this Statement means The Northwestern Mutual Life Insurance Company, Northwestern Long Term Care Insurance Company, Northwestern Mutual Investment Services, LLC, Northwestern Mutual Wealth Management Company, and the network offices of our Sales Force. Our Sales Force includes the people who sell our products and services, along with their staff.
            </Typography>
            <Typography nextElement='typography'>
              References to &#34;we,&#34; &#34;us,&#34; &#34;our,&#34; &#34;ours,&#34; or &#34;Northwestern Mutual&#34; means the &#34;Northwestern Mutual Family of Companies,&#34; and Northwestern Mutual Investment Management Company, LLC.
            </Typography>
            <Typography nextElement='typography'>
              California residents: If you are a California resident, California law may provide you with additional rights regarding your personal information. To learn more about your California privacy rights, please visit our&nbsp;
              <Link
                linkUrl='/ccpa-privacy-policy/'
                variant='primaryDeemphasize'>
                California Consumer Privacy Policy
              </Link>.
              &nbsp;Northwestern Mutual does not share your personal information with unaffiliated third parties for third party marketing purposes.
            </Typography>
            <Typography nextElement='typography'>
              Northwestern Mutual is licensed and registered to provide products and services in the United States only&#59; therefore, the Websites
              and the information contained within them is intended for use in the United States only. By using the Websites, you understand that
              Northwestern Mutual will comply with applicable United States privacy regulations and agree that Northwestern Mutual can use your
              personal information in accordance with its Online Privacy Statement and all applicable laws and regulations.
            </Typography>
            <Typography disableBottomPadding>
              The Websites are not intended for use by anyone under 18 years of age. By using the Websites, you represent that you are 18 years of age
              or older. No one under 18 years of age should use the Websites.
            </Typography>
          </Col>
        </Row>
      </SectionContainer>
      <SectionContainer
        id='online-privacy-statement-collection-information'
        backgroundVariant='lightA'
        themeType='lightTheme'
        disableBottomPadding>
        <Row align='center'>
          <Col medium={8}>
            <Typography
              component='h2'
              nextElement='typography'>
              Collection of Information
            </Typography>
            <Typography nextElement='typography'>
              We gather information that helps us protect website users, maintain the security of our Websites, and for the purposes described below.
            </Typography>
            <Typography nextElement='typography'>
              With the exception of the Subsidiary Website, the Websites gather two types of information about people who visit the Websites
              &#40;&#34;Users&#34;&#41;:
            </Typography>
            <DescriptionList
              descriptionListData={[
                {
                  descriptionDetails: <>information and data you provide voluntarily and knowingly, including information and data you submit in or through the Client Website, Client Mobile Application, and&#47;or through optional, voluntary contact request forms&#59; and</>,
                  descriptionTerm: '1. Voluntary User Information:',
                },
                {
                  descriptionDetails: 'tracking information, including (but not limited to) IP address, mobile device, browser type, and home server information.',
                  descriptionTerm: '2. Automatically Collected Information:',
                },
              ]}
              disableBullet
            />
            <Typography nextElement='typography'>
              The Subsidiary Website only gathers the Automatically Collected Information.
            </Typography>
            <Typography disableBottomPadding>
              Northwestern Mutual does not disclose any of your personal information collected on or through the Websites without your permission, except within the Northwestern Mutual Family of Companies, as described in this Statement, or as permitted or required by law. These Websites do not enable unaffiliated third parties to collect personal information about your online activities over time and across different websites when you visit these Websites, on behalf of parties other than Northwestern Mutual. Northwestern Mutual does not sell or rent your information to third parties.
            </Typography>
          </Col>
        </Row>
      </SectionContainer>
      <SectionContainer
        id='online-privacy-statement-voluntary-user-information'
        backgroundVariant='lightA'
        themeType='lightTheme'
        disableBottomPadding>
        <Row align='center'>
          <Col medium={8}>
            <Typography
              component='h2'
              nextElement='typography'>
              Voluntary User Information
            </Typography>
            <Typography disableBottomPadding>
              We generally collect information you provide voluntarily and knowingly through two main sections of the Websites: &#40;a&#41; the Client Website, accessible only after you log in with your access credentials; and &#40;b&#41; contact request forms. Northwestern Mutual uses information for the purpose described in this Statement or at the point of collection, and shares the information you submit with the Northwestern Mutual Family of Companies to service your request, for internal purposes described herein, or as otherwise permitted or required by law. We share any such information with third parties only to the extent necessary to service your request as described herein, or as otherwise permitted or required by law.
            </Typography>
          </Col>
        </Row>
      </SectionContainer>
      <SectionContainer
        id='online-privacy-statement-client-website-and-mobile-application'
        backgroundVariant='lightA'
        themeType='lightTheme'
        disableBottomPadding>
        <Row align='center'>
          <Col medium={8}>
            <Typography
              component='h2'
              nextElement='typography'>
              Client Website and Client <WidowBlock>Mobile Application</WidowBlock>
            </Typography>
            <Typography nextElement='typography'>
              You can access the Client Website &#40;&#34;Site&#34;&#41; and Client Mobile Application &#40;&#34;Mobile App&#34;&#41; which are
              collectively referred to as the &#34;Services&#34; by entering your credentials on a &#34;Log In&#34; page on the Services. Once you have entered the Services, you can access certain policy and account information, make limited service requests, view certain documents, and conduct limited transactions. You may also be asked to voluntarily provide information about yourself, and&#47;or your policy or account. In the case of corporate&#45;owned policies, you may be asked to provide information about the corporate owner of the policy or those individuals who are insured under the corporate owned policy.
            </Typography>
            <Typography nextElement='typography'>
              Some users may be directed to enter product application information on the Services, which may include personal and health information &#40;&#34;Online Application Information&#34;&#41;. Online Application Information will be used for underwriting purposes and other permissible business purposes. Online Application Information entered by you will be temporarily available on the Services for you to continue and complete the application and submission process. We will retain Online Application Information in accordance with applicable law, this Statement, our&nbsp;
              <Link
                linkUrl='/customer-privacy-notice/'
                variant='primaryDeemphasize'>
                Customer Privacy Notice
              </Link>
              , and our internal record retention policies, even if the application is not electronically signed and submitted. We will only consider  Online Application Information as part of the underwriting process after it has been incorporated into the electronically signed
              application form and submitted to us.
            </Typography>
            <Typography nextElement='typography'>
              Portions of the Services are hosted by third&#45;party service providers, which provide certain features and functionality of the
              Services. To use these features or functionality, a third party may request to share your information with Northwestern Mutual. Though each of these third parties is contractually bound to maintain the confidentiality of any information submitted by you and to use such information solely for the purpose of providing the requested services to or on behalf of the Northwestern Mutual Family of Companies, each of these third parties govern your information in accordance with its own privacy policy. For more information about the Services, please review the&nbsp;
              <Link
                linkUrl='https://plan.northwesternmutual.com/importantinfo#Terms'
                variant='primaryDeemphasize'>
                Client Website Terms of Use
              </Link>
              , available when you register or login.
            </Typography>
            <Typography disableBottomPadding>
              Information you enter into sections of the Services may be accessible to the Northwestern Mutual Family of Companies. We may also track the pages that you view, including page interactions, and may share this information with the Northwestern Mutual Family of Companies to market and offer products, engage in planning, enhance our services, and for other business purposes.
            </Typography>
          </Col>
        </Row>
      </SectionContainer>
      <SectionContainer
        id='online-privacy-statement-contact-request-forms'
        backgroundVariant='lightA'
        themeType='lightTheme'
        disableBottomPadding>
        <Row align='center'>
          <Col medium={8}>
            <Typography
              component='h2'
              nextElement='typography'>
              Contact Request Forms
            </Typography>
            <Typography nextElement='typography'>
              You may submit information through forms on the Websites for various purposes. Any information you voluntarily submit is only used by the Northwestern Mutual Family of Companies for the purpose of the submission or as specifically described below. The forms described below are a sample of the most&#45;accessed forms on the Websites.
            </Typography>
            <DescriptionList
              descriptionListData={[
                {
                  descriptionTerm: 'Contacting a Financial Professional:',
                  descriptionDetails:
                    'When you request to be contacted by a Northwestern Mutual Financial Professional, we will ask you to provide basic contact and financial information. We may share this information with the Northwestern Mutual Family of Companies to better fulfill your request and for internal marketing, customer relationship management, and servicing purposes.',
                },
                {
                  descriptionTerm: 'Becoming a Financial Professional:',
                  descriptionDetails:
                    'If you are interested in obtaining information about becoming a Northwestern Mutual Financial Professional and indicate that you would like to have a Northwestern Mutual network office recruiter contact you, we will request certain information to facilitate that request. Information you submit in this regard will be routed in an unencrypted format to Northwestern Mutual and will then be shared with the local Northwestern Mutual office that you select during the application process.',
                },
                {
                  descriptionTerm: 'Corporate Opportunities:',
                  descriptionDetails: (
                    <>
                      If you apply for a position at Northwestern Mutuals corporate offices, you will be asked to register and complete a personal profile to help match your career preference to available positions. The profile includes &#40;but is not limited to&#41; fields like contact information, work experience, skills, resume&#47;CV, career preferences, education, and location. You may also create a personal profile to join our talent community. The profile includes &#40;but is not limited to&#41; contact information, areas of interest, employment history, and location preferences. You can opt&#45;in to receive e&#45;mail notifications of available positions, or you may view and search for available positions without creating a profile. We may also create a personal profile about you using publicly available information. If we do so, we will provide you with an e&#45;mail notice of the creation of such personal profile and give you the opportunity to request deletion of the profile for purposes of contacting you about employment opportunities. Our corporate careers website is hosted and integrated with third&#45;party service providers, and as a result, the information collected through the completion of a personal profile is shared with that service provider to help facilitate workforce management. When asked to participate you may be provided links to those service providers notices.
                    </>),
                },
                {
                  descriptionTerm: 'Retirement Calculator:',
                  descriptionDetails:
                    'When you request an estimate through the retirement calculator functionality, we will ask you to provide basic contact and financial information. We may share this information with the Northwestern Mutual Family of Companies to better fulfill your request and for internal marketing, customer relationship management, and servicing purposes.',
                },
                {
                  descriptionTerm: 'Insurance Calculator:',
                  descriptionDetails:
                    'When you request an estimate through the insurance calculator functionality, we will ask you to provide basic contact and financial information. We may share this information with the Northwestern Mutual Family of Companies to better fulfill your request and for internal marketing, customer relationship management, and servicing purposes.',
                },
                {
                  descriptionTerm: 'Claims Experience:',
                  descriptionDetails:
                    'When you report a claim, we will ask you to provide personal information, medical information, contact information, occupation information (for disability claims) and product information to help facilitate the claims process. This information will be used for the claims process and sharing of this information will only occur in accordance with our Customer Privacy Notices.',
                },
                {
                  descriptionTerm: 'Innovation.NM.com Submissions:',
                  descriptionDetails:
                    'If you submit a request for us to contact you regarding new ideas you may have, we will ask you to provide basic contact information as well as details around your idea. We may share this information with the Northwestern Mutual Family of Companies to review and evaluate your idea.',
                },
                {
                  descriptionTerm: 'Other Forms and Interactive Features:',
                  descriptionDetails:
                    'Any other interactive forms or calculators on the Website are for informational purposes only. Northwestern Mutual does not track, retain or use any of the data input into these forms.',
                },
              ]}
            />
          </Col>
        </Row>
      </SectionContainer>
      <SectionContainer
        id='online-privacy-statement-automatically-collected-information'
        backgroundVariant='lightA'
        themeType='lightTheme'
        disableBottomPadding>
        <Row align='center'>
          <Col medium={8}>
            <Typography
              component='h2'
              nextElement='typography'>
              Automatically Collected Information
            </Typography>
            <Typography nextElement='typography'>
              We collect information automatically from the Websites through a variety of technologies, including: &#40;a&#41; cookies&#47;web beacons; and &#40;b&#41; third&#45;party applications.
            </Typography>
            <Typography disableBottomPadding>
              Websites that use tracking technologies to share information for our marketing purposes honor an industry accepted opt&#45;out preference signal. However, Websites that do not use tracking technologies to share information for our marketing purposes do not contain functionality to honor web browser &#34;do not track&#34; signals or other similar mechanisms, and thus do not honor such signals. Most web browsers allow you to block, manage, or delete cookies or local storage through the privacy features of your browser. Please refer to the help section of your browser or mobile device for additional information. Please note that if you block cookies or similar technologies, you may not be able to use the full functionality of the Websites.
            </Typography>
          </Col>
        </Row>
      </SectionContainer>
      <SectionContainer
        id='online-privacy-statement-cookies-and-web-beacons'
        backgroundVariant='lightA'
        themeType='lightTheme'
        disableBottomPadding>
        <Row align='center'>
          <Col medium={8}>
            <Typography
              component='h2'
              nextElement='typography'>
              Cookies and <WidowBlock>Web Beacons</WidowBlock>
            </Typography>
            <Typography nextElement='typography'>
              The Websites use various storage technologies related to online marketing tools, such as managing our advertising on other Internet
              websites. In connection with such activities, these Websites collect or receive information including &#40;but not limited to&#41; your IP address, device type, date and time of visit, browser type, and activities and the above information is collected or received by or shared with third&#45;party vendors who assist us with these activities.
            </Typography>
            <Typography nextElement='typography'>
              A cookie is a small text file that is sent to a browser when you access the Websites. The Websites do not require you to use cookies, but it is possible that certain services may not function properly if you choose to refuse to receive a cookie. We use session cookies to ensure better performance and a more enjoyable and consistent user experience from your entry point into the Websites. The session cookie is available only during the course of the browser session; the information is not sent to or stored on a computer&#39;s hard drive. Closing the browser or logging off will invalidate the session cookie. We also use machine cookies, which do remain on a computer hard drive, to gather aggregated, non&#45;identifiable website statistics through the website analytic services. No personal information is collected using this technology.
            </Typography>
            <Typography disableBottomPadding>
              We also contract with third&#45;parties to distribute e&#45;mails on our behalf, perform analytics, and to deliver online advertisements on the Websites, on other websites and on other applications. Those companies may use third&#45;party cookies, or other devices such as website scripting, pixels, or action tags to collect anonymous information about you when you visit the Websites. We use this information to learn which online advertisements attracted you, and to help us analyze the Websites and the effectiveness of our marketing.
            </Typography>
          </Col>
        </Row>
      </SectionContainer>
      <SectionContainer
        id='online-privacy-statement-tracking-technology-and-information'
        backgroundVariant='lightA'
        themeType='lightTheme'
        disableBottomPadding>
        <Row align='center'>
          <Col medium={8}>
            <Typography
              component='h2'
              nextElement='typography'>
              Tracking Technology <WidowBlock>and Information</WidowBlock>
            </Typography>
            <Typography nextElement='typography'>
              To enable us to continually improve on the content we offer you, as well as for purposes of
              personalization, analyzing user behavior, enhancing navigation, systems administration, site security, and
              fraud detection and prevention we capture IP addresses and track your activity on the Websites.
            </Typography>
            <Typography nextElement='typography'>
              To assist us in this process, the Websites use third&#45;party website analytic services that use cookies
              and web beacons to collect various information regarding users&#39; use of the Websites, including daily
              unique visitors, page views, length of time of page views, page navigation paths, time of day of visits,
              browser type used by users, the Internet Service Provider used to connect to the Internet and the Websites,
              and the type of operating system used by users.
            </Typography>
            <Typography nextElement='typography'>
              We also use third&#45;party service providers to track information about the performance of the Websites. The services collect
              information from visits to the Websites to help resolve problems with performance, such as errors in Java Script code or
              page load times. We also use this information to optimize your experience with the Websites. The collected data is information that
              Northwestern Mutual does not directly relate to a specific individual.
            </Typography>
            <Typography nextElement='typography'>
              Our website analytic and performance services providers include &#40;but are not limited to&#41; the following:
            </Typography>
            <BulletedList bulletedListData={bulletedListConfigTrackingTechnology} />
            <Typography nextElement='typography'>
              Google Maps is a third&#45;party service we use to validate and normalize addresses entered by you into the Websites, with the exception of the Subsidiary Website, through an Application Program Interface, a technology that connects Google to the Websites and allows the limited exchange of information. This information is used and retained by us only for the purpose you enter the information, which may include combination with other identifying factors you enter into the Websites to fulfill your request to be connected with a Northwestern Mutual Financial Professional in your geographical area. By entering physical address attributes into the Websites, you agree to be bound by the&nbsp;
              <Link
                linkUrl='https://maps.google.com/help/terms_maps.html'
                isOutgoingLink
                variant='primaryDeemphasize'>
                Google Maps&#47;Google Earth Additional Terms of Service.
              </Link> We do not track, obtain, or cache your location in any manner except with your prior consent, and you may revoke your consent at any time.
            </Typography>
            <Typography nextElement='typography'>
              In addition, we partner with other third&#45;parties, including third&#45;party advertising networks and social media websites, to
              manage our advertising on other Internet websites. Our third&#45;party partners may use cookies, web beacons, JavaScript tags, and other storage technologies to collect or receive information about an IP address, device type, date and time of visit, browser type, and your activities on those websites or elsewhere on the Internet and use such information to provide measurement services and targeted advertising for us.
            </Typography>
            <Typography disableBottomPadding>
              For those websites that share information for our marketing purposes, you may use the &#8220;Do Not Sell or Share My Personal Information&#8221; link on our Websites, opt&#45;out button or an industry accepted opt&#45;out preference signal to opt out of marketing cookies. Additionally, each third&#45;party advertising network maintains their own database and protocols and the use of your information by such third&#45;parties is governed by each third&#45;party&#39;s privacy policy. We do not share information that directly identifies you with these third&#45;party advertising providers&#59; however, we do share information that uniquely identifies your browser or internet device. If you are interested in more information about interest&#45;based advertising, please visit the&nbsp;
              <Link
                linkUrl='https://optout.networkadvertising.org/?c=1/'
                isOutgoingLink
                variant='primaryDeemphasize'>
                Network Advertising Initiative&#39;s Consumer Opt&#45;Out Link
              </Link>
              &nbsp;or the&nbsp;
              <Link
                linkUrl='https://optout.aboutads.info/?c=2&lang=EN'
                isOutgoingLink
                variant='primaryDeemphasize'>
                Digital Advertising Alliance&#39;s Consumer Opt&#45;Out Link
              </Link> and learn how to opt&#45;out of receiving interest&#45;based advertising from companies that participate in those programs. We do not control these opt&#45;out links and are not responsible for any choices you make using these mechanisms or the continued availability or accuracy of these mechanisms. For mobile devices, you can opt out of online interest&#45;based advertising by&nbsp;
              <Link
                linkUrl='https://support.apple.com/en-us/HT202074/'
                isOutgoingLink
                variant='primaryDeemphasize'>
                turning off personal ads on iOS devices
              </Link>
              &nbsp;or&nbsp;
              <Link
                linkUrl='https://support.google.com/googleplay/answer/3405269/'
                isOutgoingLink
                variant='primaryDeemphasize'>
                opting out of ads personalization on Android devices
              </Link>.
            </Typography>
          </Col>
        </Row>
      </SectionContainer>
      <SectionContainer
        id='online-privacy-statement-hyperlinks'
        backgroundVariant='lightA'
        themeType='lightTheme'
        disableBottomPadding>
        <Row align='center'>
          <Col medium={8}>
            <Typography
              component='h2'
              nextElement='typography'>
              Hyperlinks
            </Typography>
            <Typography nextElement='typography'>
              Our Statement applies solely to the Websites. The Websites may contain links to other Internet websites. Northwestern Mutual is not
              responsible for the content or privacy practices of these other websites, and these links do not imply an affiliation between
              Northwestern Mutual and the website owner, or any endorsement, approval, or verification of any content contained on other websites. The privacy policy of any other Internet website governs information about the collection, use, monitoring, and handling of personal information by that website.
            </Typography>
            <Typography disableBottomPadding>
              The Websites may also contain links to the Northwestern Mutual pages of several third&#45;party social media websites and additional links to third&#45;party websites that may appear as a &#34;like&#34; or &#34;share&#34; button. When you click on any such links, the third&#45;party website may send a session cookie or a persistent cookie to your computer. Please review the privacy policies of such third&#45;party social media websites for information regarding their online privacy practices.
            </Typography>
          </Col>
        </Row>
      </SectionContainer>
      <SectionContainer
        id='online-privacy-statement-notice-for-consumers-with-disabilities'
        backgroundVariant='lightA'
        themeType='lightTheme'
        disableBottomPadding>
        <Row align='center'>
          <Col medium={8}>
            <Typography
              component='h2'
              nextElement='typography'>
              Notice for Consumers <WidowBlock>with Disabilities</WidowBlock>
            </Typography>
            <Typography disableBottomPadding>
              We are committed to providing consumers with disabilities with the same level of access to this Statement that is available to consumers without disabilities. This notice is compatible with standard screen readers. If you need further assistance, please contact&nbsp;
              <Link
                id='phone-number-link'
                variant='primaryDeemphasize'
                linkUrl={clientServicesPhoneNumberLinkUrl}
                noWrap>
                <ClientServicesPhone4644 />
              </Link>.
            </Typography>
          </Col>
        </Row>
      </SectionContainer>
      <SectionContainer
        id='online-privacy-statement-changes-and-revisions'
        backgroundVariant='lightA'
        themeType='lightTheme'
        disableBottomPadding>
        <Row align='center'>
          <Col medium={8}>
            <Typography
              component='h2'
              nextElement='typography'>
              Changes and Revisions
            </Typography>
            <Typography disableBottomPadding>
              We reserve the right to amend this Statement at any time in our sole discretion. Please look at the &#34;Last Revised&#34; legend at the top of this page to see when the last revision was made. Any changes will become effective when we post the revised Statement on the Websites or Services. Your use of the Websites or Services following these changes means that you accept the revised Statement.
            </Typography>
          </Col>
        </Row>
      </SectionContainer>
      <SectionContainer
        id='online-privacy-statement-for-more-information'
        backgroundVariant='lightA'
        themeType='lightTheme'>
        <Row align='center'>
          <Col medium={8}>
            <Typography
              component='h2'
              nextElement='typography'>
              For More Information
            </Typography>
            <Typography disableBottomPadding>
              The Websites are maintained by&nbsp;
              <address>
                The Northwestern Mutual Life Insurance Company, 720 E. Wisconsin Avenue, Milwaukee, WI 53202.
              </address>
              Visit the&nbsp;
              <Link
                linkUrl='/contact-us/'
                variant='primaryDeemphasize'>
                &#34;Contact Us&#34;
              </Link>
              &nbsp;section for additional information.
            </Typography>
          </Col>
        </Row>
      </SectionContainer>
      <LegalLinks />
    </Page>
  );
};
